import type { Rect } from '../../../engine/hex/types';
import type { IconRegistry } from '../../../ui/overlay/assets/IconRegistry';
import type { ModalTheme } from '../ModalTheme';
import type { HitTarget, MeasureResult, UiItem } from './Types';

type ThemeFn<T> = T | ((theme: ModalTheme) => T);

function resolve<T>(v: ThemeFn<T> | undefined, theme: ModalTheme, fallback: T): T {
    if (v === undefined) return fallback;
    if (typeof v === 'function') return (v as (t: ModalTheme) => T)(theme);
    return v;
}

export type GridAlign = 'start' | 'center' | 'end';

export type GridLayoutStyle = {
    // If undefined: auto columns based on minCellW.
    columns?: ThemeFn<number> | 'auto';
    minCellW?: ThemeFn<number>; // used when columns === 'auto' or undefined
    maxColumns?: ThemeFn<number>; // clamp for auto columns

    gap?: ThemeFn<number>; // default: theme.spacing.itemGap
    padding?: ThemeFn<number>; // default: 0

    // If childWidthMode is 'fill' we pass full cell width to child.layout()
    // If 'wrap', we pass child’s measured width and align it in the cell.
    childWidthMode?: 'fill' | 'wrap';
    alignX?: GridAlign; // only used when childWidthMode === 'wrap'
    alignY?: GridAlign; // vertical alignment inside row (since row height is max of children)

    // Optional container background (rare; usually cell boxes draw their own)
    backgroundFill?: ThemeFn<string | null>;
    backgroundStroke?: ThemeFn<string | null>;
    strokeWidth?: ThemeFn<number>;
};

export class GridLayoutItem implements UiItem {
    private rect: Rect = { x: 0, y: 0, w: 0, h: 0 };

    private columns = 1;
    private gap = 0;
    private padding = 0;

    private cellRects: Rect[] = [];
    private childSizes: MeasureResult[] = [];
    private rowHeights: number[] = [];

    constructor(
        private cells: UiItem[],
        private style: GridLayoutStyle = {},
    ) {}

    private computeColumns(innerW: number, theme: ModalTheme): number {
        const { style } = this;

        const configured = style.columns;
        if (typeof configured === 'number') return Math.max(1, configured);
        if (typeof configured === 'function') return Math.max(1, configured(theme));
        if (configured && configured !== 'auto') return 1;

        const minCellW = resolve(style.minCellW, theme, 120);
        const maxColumns = resolve(style.maxColumns, theme, Number.POSITIVE_INFINITY);

        // columns = floor((innerW + gap) / (minCellW + gap))
        const raw = Math.floor((innerW + this.gap) / Math.max(1, minCellW + this.gap));
        const clamped = Math.max(1, Math.min(raw, maxColumns));
        return Math.min(clamped, Math.max(1, this.cells.length));
    }

    measure(ctx: CanvasRenderingContext2D, maxW: number, theme: ModalTheme): MeasureResult {
        this.gap = resolve(this.style.gap, theme, theme.spacing.itemGap);
        this.padding = resolve(this.style.padding, theme, 0);

        const innerW = Math.max(0, maxW - this.padding * 2);
        this.columns = this.computeColumns(innerW, theme);

        const cellW =
            this.columns > 0
                ? (innerW - this.gap * Math.max(0, this.columns - 1)) / this.columns
                : innerW;

        this.childSizes = [];
        this.cells.forEach((cell) => {
            this.childSizes.push(cell.measure(ctx, cellW, theme));
        });

        const rows = Math.ceil(this.cells.length / this.columns);
        this.rowHeights = [];
        for (let row = 0; row < rows; row += 1) {
            let rowH = 0;
            for (let col = 0; col < this.columns; col += 1) {
                const index = row * this.columns + col;
                rowH = Math.max(rowH, this.childSizes[index]?.h ?? 0);
            }
            this.rowHeights.push(rowH);
        }

        const contentH =
            this.rowHeights.reduce((sum, h) => sum + h, 0) + this.gap * Math.max(0, rows - 1);

        return { w: maxW, h: contentH + this.padding * 2 };
    }

    layout(x: number, y: number, w: number, theme: ModalTheme): void {
        // Keep geometry consistent with measure() assumptions.
        const innerW = Math.max(0, w - this.padding * 2);
        const cellW =
            (innerW - this.gap * Math.max(0, this.columns - 1)) / Math.max(1, this.columns);

        const rows = Math.ceil(this.cells.length / this.columns);
        const h =
            this.padding * 2 +
            this.rowHeights.reduce((sum, rh) => sum + rh, 0) +
            this.gap * Math.max(0, rows - 1);

        this.rect = { x, y, w, h };
        this.cellRects = new Array(this.cells.length);

        const childWidthMode = this.style.childWidthMode ?? 'fill';
        const alignX: GridAlign = this.style.alignX ?? 'start';
        const alignY: GridAlign = this.style.alignY ?? 'start';

        let cy = y + this.padding;
        for (let row = 0; row < rows; row += 1) {
            const rowH = this.rowHeights[row] ?? 0;
            for (let col = 0; col < this.columns; col += 1) {
                const index = row * this.columns + col;
                if (index >= this.cells.length) break;

                const cx = x + this.padding + col * (cellW + this.gap);
                const cellRect: Rect = { x: cx, y: cy, w: cellW, h: rowH };
                this.cellRects[index] = cellRect;

                const child = this.cells[index];
                const size = this.childSizes[index] ?? { w: cellW, h: 0 };

                const childW = childWidthMode === 'wrap' ? Math.min(cellW, size.w) : cellW;
                const extraX = Math.max(0, cellW - childW);
                const ox =
                    childWidthMode === 'wrap'
                        ? alignX === 'center'
                            ? extraX / 2
                            : alignX === 'end'
                              ? extraX
                              : 0
                        : 0;

                const extraY = Math.max(0, rowH - size.h);
                const oy = alignY === 'center' ? extraY / 2 : alignY === 'end' ? extraY : 0;

                child.layout(cx + ox, cy + oy, childW, theme);
            }
            cy += rowH + (row < rows - 1 ? this.gap : 0);
        }
    }

    draw(ctx: CanvasRenderingContext2D, theme: ModalTheme, icons?: IconRegistry): void {
        const fill = resolve(this.style.backgroundFill, theme, null);
        const stroke = resolve(this.style.backgroundStroke, theme, null);
        if (fill || stroke) {
            ctx.lineWidth = resolve(this.style.strokeWidth, theme, 1);
            if (fill) {
                ctx.fillStyle = fill;
                ctx.fillRect(this.rect.x, this.rect.y, this.rect.w, this.rect.h);
            }
            if (stroke) {
                ctx.strokeStyle = stroke;
                ctx.strokeRect(this.rect.x, this.rect.y, this.rect.w, this.rect.h);
            }
        }

        this.cells.forEach((cell) => cell.draw(ctx, theme, icons));
    }

    collectHitTargets(out: HitTarget[], scrollY: number): void {
        this.cells.forEach((cell) => cell.collectHitTargets(out, scrollY));
    }

    getCellRect(index: number): Rect | null {
        return this.cellRects[index] ?? null;
    }
}

export class HoverGridItem<T> implements UiItem {
    private grid: GridLayoutItem;
    private entries: T[];

    constructor(
        entries: T[] | null | undefined,
        private cells: UiItem[],
        private style: GridLayoutStyle = {},
    ) {
        this.entries = entries ?? [];
        this.grid = new GridLayoutItem(cells, style);
    }

    measure(ctx: CanvasRenderingContext2D, maxW: number, theme: ModalTheme): MeasureResult {
        return this.grid.measure(ctx, maxW, theme);
    }

    layout(x: number, y: number, w: number, theme: ModalTheme): void {
        this.grid.layout(x, y, w, theme);
    }

    draw(ctx: CanvasRenderingContext2D, theme: ModalTheme, icons?: IconRegistry): void {
        this.grid.draw(ctx, theme, icons);
    }

    collectHitTargets(out: HitTarget[], scrollY: number): void {
        this.grid.collectHitTargets(out, scrollY);
    }

    getHoverEntry(
        point: { x: number; y: number },
        scrollY: number,
    ): { entry: T; rect: Rect } | null {
        if (!this.entries || !this.entries.length) return null;
        for (let index = 0; index < this.entries.length; index += 1) {
            const rect = this.grid.getCellRect(index);
            const entry = this.entries[index];
            if (!rect || !entry) continue;
            const screenRect = { x: rect.x, y: rect.y - scrollY, w: rect.w, h: rect.h };
            if (
                point.x >= screenRect.x &&
                point.x <= screenRect.x + screenRect.w &&
                point.y >= screenRect.y &&
                point.y <= screenRect.y + screenRect.h
            ) {
                return { entry, rect: screenRect };
            }
        }
        return null;
    }
}
